import JSZip from 'jszip';
import { FloorplanRevision } from '@thive/fddm-types';
import { useCallback, useMemo } from 'react';

import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { ValidFileExtension } from '@/modules/vehicles/types';
import { logger } from '@common/logging/logger';
import {
  PROJECT_FILES_ROOT_CONTAINER,
  KM_MDB_ASSET_FILE_CONTAINER,
  UVT_ASSET_FILE_CONTAINER,
  REFERENCE_IMAGE_FILE_CONTAINER,
} from '@/modules/api/constant';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import { isObject } from '@/modules/common/helpers/file';
import { VehiclesUtil } from '@/modules/vehicles/vehiclesUtil';

import { useFloorplanObjectValidation } from './useFloorplanObjectValidation';

type KmMdbFile = {
  vehicleVariantId: string;
  content: Blob;
};

type UvtFile = {
  vehicleVariantId: string;
  content: string;
};

type SimulationFile = {
  content: string;
  name: string;
};

export type ParsedZipFile = {
  kmMdbFiles: KmMdbFile[];
  uvtFiles: UvtFile[];
  fmsConfigFiles: SimulationFile[];
  orderProfileFiles: SimulationFile[];
  floorplan: undefined | FloorplanRevision;
  referenceImageData:
    | undefined
    | {
        file: Blob;
        fileName: string;
      };
};

export const useZipFileProcessor = () => {
  const { isValidFloorplan } = useFloorplanObjectValidation();
  const fileApi = useMemo(() => FileServiceConnector.getInstance(), []);

  const uploadAssetFileToFileStorage = useCallback(
    async (
      file: Blob,
      fileRef: string,
      extension: ValidFileExtension | 'csv',
      storageBasePath?: string,
    ) => {
      try {
        const basePath = storageBasePath ?? buildRevisionFileStorageBasePath();
        if (!basePath) throw Error('basePath not defined.');

        await fileApi.addFile(
          `${basePath}/${fileRef}`,
          file,
          extension,
          PROJECT_FILES_ROOT_CONTAINER,
        );

        return { success: true };
      } catch (error) {
        logger.error('Error uploading file:', error);
        return { success: false };
      }
    },
    [fileApi],
  );

  const parseZipFile = useCallback(
    async (zip: JSZip): Promise<ParsedZipFile> => {
      const kmMdbFiles: KmMdbFile[] = [];
      const uvtFiles: UvtFile[] = [];
      const fmsConfigFiles: SimulationFile[] = [];
      const orderProfileFiles: SimulationFile[] = [];
      let floorplan: FloorplanRevision | undefined;
      let referenceImageData: ParsedZipFile['referenceImageData'];

      await Promise.all(
        Object.keys(zip.files).map(async (relativePath) => {
          const zipEntry = zip.files[relativePath];
          if (zipEntry.dir) return;

          if (relativePath.startsWith('referenceImage/')) {
            const fileContent = await zipEntry.async('blob');
            const fileName = zipEntry.name.split('/')[1];
            referenceImageData = {
              file: fileContent,
              fileName,
            };
            return;
          }

          if (relativePath.startsWith('kmMdb/')) {
            const fileContent = await zipEntry.async('blob');
            kmMdbFiles.push({
              vehicleVariantId: relativePath.slice(6, -4),
              content: fileContent,
            });
            return;
          }

          if (relativePath.startsWith('uvt/')) {
            const fileContent = await zipEntry.async('string');
            uvtFiles.push({
              vehicleVariantId: relativePath.slice(4, -5),
              content: fileContent,
            });
            return;
          }

          if (relativePath.startsWith('fms_config/')) {
            const fileContent = await zipEntry.async('string');
            fmsConfigFiles.push({
              content: fileContent,
              name: zipEntry.name,
            });
            return;
          }

          if (relativePath.startsWith('order_profile/')) {
            const fileContent = await zipEntry.async('string');
            orderProfileFiles.push({
              content: fileContent,
              name: zipEntry.name,
            });
            return;
          }

          if (relativePath.endsWith('.json')) {
            const parsedFloorplan: unknown = JSON.parse(await zipEntry.async('string'));
            if (!isObject(parsedFloorplan) || !isValidFloorplan(parsedFloorplan)) {
              logger.error('Invalid floorplan:', parsedFloorplan);
              return;
            }

            floorplan = parsedFloorplan;
            return;
          }

          logger.error('Unrecognized file in ZIP:', relativePath);
        }),
      );

      return {
        kmMdbFiles,
        uvtFiles,
        fmsConfigFiles,
        orderProfileFiles,
        floorplan,
        referenceImageData,
      };
    },
    [isValidFloorplan],
  );

  const readZipFile = useCallback(
    async (file: File): Promise<ParsedZipFile | null> => {
      try {
        const zip = await JSZip.loadAsync(await file.arrayBuffer());
        const parsedZipFile = await parseZipFile(zip);

        if (!parsedZipFile.floorplan) {
          logger.error('Floorplan JSON not found in the ZIP file.');
          return null;
        }

        return parsedZipFile;
      } catch (error) {
        logger.error('Error reading ZIP file:', error);
        return null;
      }
    },
    [parseZipFile],
  );

  const uploadFile = useCallback(
    async (
      fileContent: Blob,
      type: ValidFileExtension,
      vehicleName: string,
      storageBasePath?: string,
    ) => {
      const containerName =
        type === 'json' ? UVT_ASSET_FILE_CONTAINER : KM_MDB_ASSET_FILE_CONTAINER;

      const newFileRef = VehiclesUtil.buildVehicleAssetFileRef(containerName, vehicleName, type);
      const result = await uploadAssetFileToFileStorage(
        fileContent,
        newFileRef,
        type,
        storageBasePath,
      );

      if (!result.success) {
        logger.error(`Failed to upload '${type}' asset to file API:`, newFileRef);
        return null;
      }

      return newFileRef;
    },
    [uploadAssetFileToFileStorage],
  );

  const processKmMdbFiles = useCallback(
    async (kmMdbFiles: KmMdbFile[], floorplan: FloorplanRevision, storageBasePath: string) => {
      await Promise.all(
        kmMdbFiles.map(async (file) => {
          const newFileRef = await uploadFile(
            new Blob([file.content], { type: 'application/vnd.ms-access' }),
            'mdb',
            file.vehicleVariantId,
            storageBasePath,
          );

          if (!newFileRef) return;

          const matchingAsset = floorplan.setup.vehicleTypes.find(
            (item) => item.id === file.vehicleVariantId,
          );

          if (matchingAsset) {
            matchingAsset.kmMdbFileRef = newFileRef;
            matchingAsset.kmMdbFileRefOriginal = false;
          }
        }),
      );
    },
    [uploadFile],
  );

  const processUvtFiles = useCallback(
    async (uvtFiles: UvtFile[], floorplan: FloorplanRevision, storageBasePath: string) => {
      await Promise.all(
        uvtFiles.map(async (file) => {
          const newFileRef = await uploadFile(
            new Blob([file.content], { type: 'application/json' }),
            'json',
            file.vehicleVariantId,
            storageBasePath,
          );

          if (!newFileRef) return;

          const matchingAsset = floorplan.setup.vehicleTypes.find(
            (item) => item.id === file.vehicleVariantId,
          );

          if (matchingAsset) {
            matchingAsset.uvtFileRef = newFileRef;
            matchingAsset.uvtFileRefOriginal = false;
          }
        }),
      );
    },
    [uploadFile],
  );

  const processFmsConfigFiles = useCallback(
    async (fmsConfigFiles: SimulationFile[], storageBasePath: string) => {
      await Promise.all(
        fmsConfigFiles.map(async (file) => {
          await uploadAssetFileToFileStorage(
            new Blob([file.content], { type: 'application/json' }),
            file.name,
            'json',
            storageBasePath,
          );
        }),
      );
    },
    [uploadAssetFileToFileStorage],
  );

  const processOrderProfileFiles = useCallback(
    async (orderProfileFiles: SimulationFile[], storageBasePath: string) => {
      await Promise.all(
        orderProfileFiles.map(async (file) => {
          await uploadAssetFileToFileStorage(
            new Blob([file.content], { type: 'text/csv' }),
            file.name,
            'csv',
            storageBasePath,
          );
        }),
      );
    },
    [uploadAssetFileToFileStorage],
  );

  const processReferenceImage = useCallback(
    async (referenceImageData: ParsedZipFile['referenceImageData'], storageBasePath: string) => {
      if (!referenceImageData) return;

      const { file, fileName } = referenceImageData;

      const parts = fileName.split('.');
      const extension = parts[parts.length - 1] as unknown as any;

      await fileApi.addFile(
        `${storageBasePath}/${REFERENCE_IMAGE_FILE_CONTAINER}/${fileName}`,
        file,
        extension,
        PROJECT_FILES_ROOT_CONTAINER,
      );
    },
    [fileApi],
  );

  const processZipFile = useCallback(
    async (
      parsedZipFile: ParsedZipFile,
      storageBasePath: string,
    ): Promise<FloorplanRevision | null> => {
      if (!parsedZipFile.floorplan) return null;

      const floorplan = structuredClone(parsedZipFile.floorplan);

      if (parsedZipFile.kmMdbFiles.length > 0) {
        await processKmMdbFiles(parsedZipFile.kmMdbFiles, floorplan, storageBasePath);
      }

      if (parsedZipFile.uvtFiles.length > 0) {
        await processUvtFiles(parsedZipFile.uvtFiles, floorplan, storageBasePath);
      }

      if (parsedZipFile.fmsConfigFiles.length > 0) {
        processFmsConfigFiles(parsedZipFile.fmsConfigFiles, storageBasePath);
      }

      if (parsedZipFile.orderProfileFiles.length > 0) {
        processOrderProfileFiles(parsedZipFile.orderProfileFiles, storageBasePath);
      }

      if (parsedZipFile.referenceImageData) {
        await processReferenceImage(parsedZipFile.referenceImageData, storageBasePath);
      }

      if (floorplan.uxonly) {
        floorplan.uxonly.thumbnail = '';
      }
      return floorplan;
    },
    [
      processKmMdbFiles,
      processUvtFiles,
      processFmsConfigFiles,
      processOrderProfileFiles,
      processReferenceImage,
    ],
  );

  const createAssetZip = useCallback(
    async (
      uvtFiles: object[],
      uvtFileNames: string[],
      kmMdbFiles: Blob[],
      kmMdbFileNames: string[],
      fmsConfigFiles: object[],
      fmsConfigFileNames: string[],
      orderProfileFiles: Blob[],
      orderProfileFileNames: string[],
      floorplan: File,
      referenceImageData:
        | {
            blob: Blob;
            fileName: string;
          }
        | undefined,
    ) => {
      const zip = new JSZip();

      if (!floorplan || !uvtFiles.length) {
        throw new Error('No floorplan or UVT files to zip');
      }

      if (kmMdbFiles.length) {
        const kmMdbFolder = zip.folder('kmMdb');
        kmMdbFiles.forEach((file, index) => {
          kmMdbFolder?.file(
            `${kmMdbFileNames[index]}.mdb`,
            new Blob([file], { type: 'application/vnd.ms-access' }),
          );
        });
      }

      const uvtFolder = zip.folder('uvt');
      uvtFiles.forEach((file, index) => {
        uvtFolder?.file(
          `${uvtFileNames[index]}.json`,
          new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' }),
        );
      });

      if (fmsConfigFiles.length) {
        const fmsConfigFolder = zip.folder('fms_config');
        fmsConfigFiles.forEach((file, index) => {
          fmsConfigFolder?.file(
            `${fmsConfigFileNames[index]}`,
            new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' }),
          );
        });
      }

      if (orderProfileFiles.length) {
        const orderProfileFolder = zip.folder('order_profile');
        orderProfileFiles.forEach((file, index) => {
          orderProfileFolder?.file(
            `${orderProfileFileNames[index]}`,
            new Blob([file], {
              type: 'text/csv;charset=utf-8',
            }),
          );
        });
      }

      if (referenceImageData) {
        const refImageFolder = zip.folder('referenceImage');
        refImageFolder?.file(referenceImageData.fileName, referenceImageData.blob);
      }

      zip.file(floorplan.name || 'floorplan.json', floorplan);
      return zip.generateAsync({ type: 'blob' });
    },
    [],
  );

  return {
    createAssetZip,
    processZipFile,
    readZipFile,
  };
};
